import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    MeetingGuestListParams,
    MeetingGuestRequest,
} from '@/lib/api/meeting-guest-api'
import { meetingGuestApi } from '@/lib/api/meeting-guest-api'
import { formatApiError } from '@/lib/format-api-error'
import { m } from '@/lib/paraglide/messages'

export const meetingGuestKeys = {
    all: ['meeting-guests'] as const,
    list: (params: MeetingGuestListParams) =>
        [...meetingGuestKeys.all, 'list', params] as const,
    infinite: (params: MeetingGuestListParams) =>
        [...meetingGuestKeys.all, 'infinite', params] as const,
}

/**
 * Danh bạ khách mời cho ô chọn trong form lịch họp: tìm trên server, cuộn tới
 * đâu tải tới đó.
 *
 * Không nạp cả danh bạ rồi lọc ở máy: danh sách này chỉ dài thêm theo năm
 * tháng, lọc ở client sẽ hỏng dần mà không ai để ý.
 */
export function useMeetingGuestsInfinite(params: MeetingGuestListParams = {}) {
    return useInfiniteQuery({
        queryKey: meetingGuestKeys.infinite(params),
        queryFn: ({ pageParam }) =>
            meetingGuestApi.list({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

/** Danh bạ phân trang cho màn quản lý. */
export function useMeetingGuests(params: MeetingGuestListParams) {
    return useQuery({
        queryKey: meetingGuestKeys.list(params),
        queryFn: () => meetingGuestApi.list(params),
    })
}

function useInvalidateMeetingGuests() {
    const qc = useQueryClient()
    return () => {
        void qc.invalidateQueries({ queryKey: meetingGuestKeys.all })
    }
}

export function useCreateMeetingGuest() {
    const invalidate = useInvalidateMeetingGuests()
    return useMutation({
        mutationFn: (body: MeetingGuestRequest) => meetingGuestApi.create(body),
        onSuccess: () => invalidate(),
        onError: (err) => {
            const p = formatApiError(err, m.meeting_guest_save_error())
            toast.error(p.title, { description: p.description })
        },
    })
}

export function useUpdateMeetingGuest() {
    const invalidate = useInvalidateMeetingGuests()
    return useMutation({
        mutationFn: ({ id, body }: { id: string; body: MeetingGuestRequest }) =>
            meetingGuestApi.update(id, body),
        onSuccess: () => {
            invalidate()
            toast.success(m.meeting_guest_saved())
        },
        onError: (err) => {
            const p = formatApiError(err, m.meeting_guest_save_error())
            toast.error(p.title, { description: p.description })
        },
    })
}

export function useDeleteMeetingGuest() {
    const invalidate = useInvalidateMeetingGuests()
    return useMutation({
        mutationFn: (id: string) => meetingGuestApi.remove(id),
        onSuccess: () => {
            invalidate()
            toast.success(m.meeting_guest_deleted())
        },
        onError: (err) => {
            const p = formatApiError(err, m.meeting_guest_delete_error())
            toast.error(p.title, { description: p.description })
        },
    })
}
